
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import BoostStore from 'stores/BoostStore';
import FirebaseStore from 'stores/FirebaseStore';
import PaymentStore from 'stores/PaymentStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import TokenUpchargeDisclaimer from 'components/TokenUpchargeDisclaimer';
import APIStates from 'enum/APIStates';
import allUserGroups from 'enum/userGroups';
import { BOOST_ERRORS } from 'enum/helpers';
import ThemeService from 'themes/ThemeService';
import { addPercentage } from 'utils/helpers';

import tokenIcon from '../../../images/tokens-icon-dark.svg';

import styles from './BoostModal.css';


const DEFAULT_BOOST_DURATION = 25;
const DEFAULT_BOOST_PRICE = 25;

// eslint-disable-next-line complexity
function Content(props) {
  const {
    close,
    insufficientBalanceError,
    isBroadcasterWindow,
    isDesktop,
    roomName,
    showBuyTokensModal,
    showGoldFeature,
    upgradeFromBoostModal,
    user,
  } = props;

  const {
    boostCam,
    boostError,
    boostStatus,
    freeBoost,
    resetBoostStatus,
  } = BoostStore;
  const { balance } = PaymentStore;
  const { user: { tokenUpchargePercentage } } = UserStore;
  const { username, accessHash } = user;

  const isLoading = boostStatus === APIStates.PENDING;
  const isSuccess = boostStatus === APIStates.LOADED;
  const duration = window.BOOSTED_ROOM_DURATION_MINUTES || 5;

  let price = window.BOOSTED_ROOM_PRICE_TOKENS || DEFAULT_BOOST_PRICE;
  if (
    !isBroadcasterWindow &&
    UserStore.hasCF(allUserGroups.USE_UPCHARGE) &&
    tokenUpchargePercentage
  ) {
    price = addPercentage(price, tokenUpchargePercentage);
  }

  const notEnoughTokens = balance < price && username;

  const extendedDuration = window.BOOSTED_ROOM_EXTENDED_DURATION_MINUTES || DEFAULT_BOOST_DURATION;
  const totalDuration = duration + extendedDuration;

  const tokenUpchargeDisclaimerElement = useMemo(() => {
    if (isBroadcasterWindow || freeBoost) {
      return null;
    }

    return (
      <TokenUpchargeDisclaimer className={styles.tokenUpchargeDisclaimer} />
    );
  }, [freeBoost, isBroadcasterWindow]);

  useEffect(() => {
    if (isSuccess) {
      close();
      resetBoostStatus(APIStates.INITIAL);
    }
  }, [isSuccess, resetBoostStatus, close]);

  useEffect(() => {
    if (isBroadcasterWindow) {
      TippingStore.getTippingInfo(
        user.username,
        user.username?.toLowerCase(),
        user,
        FirebaseStore.tippingSessionId
      );
    }
  }, [isBroadcasterWindow, user]);

  const onBoostClick = useCallback(() => {
    const boostData = {
      username,
      accessHash,
      roomName,
    };
    if (freeBoost) {
      boostData.freeBoost = freeBoost;
    }
    boostCam(boostData);
  }, [accessHash, boostCam, freeBoost, roomName, username]);

  const onUpgrade = useCallback(() => {
    upgradeFromBoostModal();
  }, [upgradeFromBoostModal]);

  const onBuyTokens = useCallback(() => {
    close();
    showBuyTokensModal();
  }, [close, showBuyTokensModal]);

  const handleBroadcasterBoostErrorText = () => {
    if (
      boostError === BOOST_ERRORS.INSUFFICIENT_FUNDS ||
      boostError === BOOST_ERRORS.EARNINGS_LOCKED
    ) {
      return translate('profile.boost.insufficient.balance.error');
    } else if (boostError === BOOST_ERRORS.ROOM_ALREADY_BOOSTED) {
      return translate('profile.boost.error.room.already.boosted');
    }

    return translate('account.errorGeneric');
  };

  const handleViewerBoostErrorText = () => {
    if (boostError === BOOST_ERRORS.ROOM_ALREADY_BOOSTED) {
      return translate('profile.boost.error.room.already.boosted.viewer');
    }

    return translate('homepage.v2.errors.generic');
  };

  const bodyClasses = classNames({
    [styles.body]: true,
    [styles.bodyMobile]: !isDesktop,
  });

  const footerClasses = classNames({
    [styles.footer]: true,
    [styles.footerMobile]: !isDesktop,
  });

  const renderBoostText = useCallback(() => {
    let boostText = undefined;
    if (!username) {
      boostText = translate('v2profile.boost.guest.message');
    } else if (isBroadcasterWindow) {
      const boostedViewerThreshHold = window.BOOSTED_ROOM_VIEWER_THRESHOLD || 10;
      boostText = translate(
        'v2broadcast.boost.broadcaster.main.text',
        { 0: boostedViewerThreshHold }
      );
    } else {
      boostText = translate(
        'profile.boost.main.text.new',
        { 0: roomName, 1: duration }
      );
    }

    return (
      <div className={styles.boostText}>
        {boostText}
      </div>
    );
  }, [
    duration,
    isBroadcasterWindow,
    roomName,
    username,
  ]);

  return (
    <Fragment>
      <div className={styles.modal}>
        <div className={styles.header}>
          <img alt="boosticon" src={ThemeService.theme.boost.icon} />
          {ThemeService.theme.boost.modalTitle}
        </div>
        <div className={bodyClasses}>
          {renderBoostText()}
          <div className={styles.boostPrice}>
            <img alt="boosticon" src={tokenIcon} />
            {freeBoost ? (
              <Fragment>
                <s className={styles.crossedOut}>
                  {price}
                </s>
                <span className={styles.oldPrice}>
                  0
                </span>
              </Fragment>
            ) : (
              price
            )}
            {' '}
            {translate('profile.boost.tokens')}
          </div>
          {tokenUpchargeDisclaimerElement}
          {showGoldFeature && (
            <div className={styles.goldInfoBox}>
              <div className={styles.icon} />
              <div className={styles.text}>
                {translate('gold.upgrade.gold.free.boost.modal')}
                {' '}
                {upgradeFromBoostModal && (
                  <span className={styles.link} onClick={onUpgrade}>
                    {translate('gold.upgrade.sales.pm.body.2')}
                  </span>
                )}
              </div>
            </div>
          )}
          {insufficientBalanceError && !isBroadcasterWindow && (
            <ErrorAlert message={translate('profile.boost.insufficient.balance.error')} />
          )}
          {isBroadcasterWindow && (
            <p className={styles.broadcasterItalic}>
              {translate(
                'v2broadcast.boost.maximum.minutes',
                { 0: totalDuration }
              )}
            </p>
          )}
          {isBroadcasterWindow && notEnoughTokens && (
            <p>
              {translate(
                'v2broadcast.boost.deducted.from.earnings',
                { 0: '<b>', 1: '</b>' }
              )}
            </p>
          )}
          {boostError && (
            <ErrorAlert
              message={isBroadcasterWindow ? handleBroadcasterBoostErrorText() : handleViewerBoostErrorText()}
            />
          )}
        </div>
        <div className={footerClasses}>
          {isDesktop && (
            <Button
              colour="transparent"
              id="boostCancel"
              label={translate('v2profile.v2.button.cancel')}
              onClick={close}
            />
          )}
          {insufficientBalanceError && !isBroadcasterWindow ? (
            <Button
              colour="green"
              id="boostBuyTokens"
              label={translate('v2profile.buy.tokens')}
              onClick={onBuyTokens}
            />
          ) : (
            <LoadingButton
              colour="orange"
              customContainerClass={styles.loadingButton}
              id="boostButton"
              isLoading={isLoading}
              onClick={onBoostClick}
            >
              {translate('profile.boost.proceed')}
            </LoadingButton>
          )}
        </div>
      </div>
    </Fragment>
  );
}

Content.propTypes = {
  close: PropTypes.func.isRequired,
  roomName: PropTypes.string.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  insufficientBalanceError: PropTypes.bool,
  isBroadcasterWindow: PropTypes.bool,
  isDesktop: PropTypes.bool,
  showGoldFeature: PropTypes.bool,
  upgradeFromBoostModal: PropTypes.func,
};

Content.defaultProps = {
  insufficientBalanceError: false,
  isBroadcasterWindow: false,
  isDesktop: false,
  showGoldFeature: false,
  upgradeFromBoostModal: undefined,
};

export default observer(Content);
